import { useState } from 'react';
import {
	ColumnDef,
	flexRender,
	getCoreRowModel,
	getFilteredRowModel,
	SortingState,
	useReactTable,
} from '@tanstack/react-table';

import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import { useTranslation } from 'react-i18next';
import { ETablePaginationWithApi } from '@/components/everridge/table/components/ETablePaginationWithApi';
import { OrderListItemResponseDto, OrderStatus } from '@/features/orders/types/orders-dto';

type ETableProps<TData, TValue> = {
	columns: ColumnDef<TData, TValue>[];
	data?: { items?: TData[]; totalCount: number };
	showDetails?: (id: string, status: OrderStatus) => void;
	onRowClick?: (row: TData) => void;
	pagination?: boolean;
	paginationStep?: number;
	totalCount?: number;
	page: number;
	setPage: (page: number) => void;
	isLoadingPage?: boolean;
};

const ETableWithApiPagination = <TData, TValue>({
	columns,
	data,
	showDetails,
	onRowClick,
	pagination = true,
	paginationStep = 10,
	page,
	setPage,
	isLoadingPage,
}: ETableProps<TData, TValue>) => {
	const isRowClickable = Boolean(onRowClick || showDetails);
	const [sorting, setSorting] = useState<SortingState>([]);
	const { t } = useTranslation();

	const table = useReactTable({
		data: data?.items ?? [],
		columns,
		getCoreRowModel: getCoreRowModel(),
		onSortingChange: setSorting,
		getFilteredRowModel: getFilteredRowModel(),
		state: {
			sorting,
		},
	});

	return (
		<>
			<div className='rounded-semi-xl border border-popover-100 bg-popover-10'>
				<Table>
					<TableHeader>
						{table.getHeaderGroups().map((headerGroup) => (
							<TableRow key={headerGroup.id}>
								{headerGroup.headers.map((header) => {
									return (
										<TableHead
											key={header.id}
											style={{
												maxWidth:
													header.column.columnDef
														.size											
											}}>
											{header.isPlaceholder
												? null
												: flexRender(
														header.column.columnDef
															.header,
														header.getContext(),
												  )}
										</TableHead>
									);
								})}
							</TableRow>
						))}
					</TableHeader>
					<TableBody>
						{data?.totalCount && data.totalCount > 0 ? (
							table.getRowModel().rows.map((row) => (
								<TableRow
									key={row.id}
									data-state={
										row.getIsSelected() && 'selected'
									}
									className={isRowClickable ? 'select-none cursor-pointer' : 'select-none'}
									onClick={() => {
										if (onRowClick) {
											onRowClick(row.original as TData);
											return;
										}
										showDetails?.(
											(row.original as OrderListItemResponseDto).id,
											(row.original as OrderListItemResponseDto).status,
										);
									}}>
									{row.getVisibleCells().map((cell) => (
										<TableCell
											key={cell.id}
											style={{
												maxWidth:
													cell.column.columnDef.size,
											}}>
											{flexRender(
												cell.column.columnDef.cell,
												cell.getContext(),
											)}
										</TableCell>
									))}
								</TableRow>
							))
						) : (
							<TableRow className={'select-none'}>
								<TableCell
									colSpan={columns.length}
									className='py-6 text-sm font-medium text-center'>
									{t('elements.table.body.empty')}
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</div>
			{pagination && !!data?.totalCount && data?.totalCount > 10 && (
				<ETablePaginationWithApi
					totalCount={Math.ceil(data?.totalCount / paginationStep)}
					page={page}
					setPage={setPage}
				/>
			)}
		</>
	);
};

export default ETableWithApiPagination;
